import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUp, ArrowDown, Plus, Trash2, Save, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

export const FIELDS = [
  ["status", "Status", "Not Started, Started, Finished or N/A"],
  ["owner", "Name (owner)", "Who is looking after the item"],
  ["notes", "Notes", "Details, links, contacts"],
  ["due_date", "Due date (optional)", "Target date, counted back from opening day"],
  ["convert_to_task", "Convert to task", "Turn an item into a real task in one click"],
];

const EMPTY = { name: "", fields: Object.fromEntries(FIELDS.map(([k]) => [k, true])), sections: [{ name: "General", items: [{ name: "", days_before: null }] }] };

const move = (arr, i, d) => {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

const FieldsPanel = ({ fields, onChange }) => (
  <aside className="rounded-2xl border border-slate-200 bg-white p-4 h-fit lg:sticky lg:top-20" data-testid="builder-fields-panel">
    <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#24474A] mb-3">Columns on each item</div>
    <div className="flex items-center gap-3 py-2 opacity-70"><Lock className="w-4 h-4 text-slate-400" /><div className="text-sm font-bold">Item name</div><span className="ml-auto text-xs text-slate-400">always</span></div>
    {FIELDS.map(([k, label, hint]) => (
      <label key={k} className="flex items-start gap-3 py-2 border-t border-slate-100 cursor-pointer">
        <div className="flex-1"><div className="text-sm font-bold text-slate-800">{label}</div><div className="text-xs text-slate-500">{hint}</div></div>
        <Switch checked={fields[k]} onCheckedChange={(v) => onChange({ ...fields, [k]: v })} data-testid={`builder-field-${k}`} />
      </label>
    ))}
  </aside>
);

const ItemRow = ({ item, si, ii, showDays, update, remove, shift }) => (
  <div className="flex items-center gap-2 px-3 py-2 border-t border-slate-100" data-testid={`builder-item-${si}-${ii}`}>
    <div className="flex flex-col">
      <button type="button" onClick={() => shift(-1)} className="text-slate-400 hover:text-[#24474A]" data-testid={`builder-item-up-${si}-${ii}`}><ArrowUp className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={() => shift(1)} className="text-slate-400 hover:text-[#24474A]" data-testid={`builder-item-down-${si}-${ii}`}><ArrowDown className="w-3.5 h-3.5" /></button>
    </div>
    <Input value={item.name} onChange={(e) => update({ ...item, name: e.target.value })} placeholder="Item name" className="h-9 text-sm flex-1" data-testid={`builder-item-name-${si}-${ii}`} />
    {showDays && (
      <div className="flex items-center gap-1.5 shrink-0">
        <Input type="number" value={item.days_before ?? ""} onChange={(e) => update({ ...item, days_before: e.target.value === "" ? null : Number(e.target.value) })}
          placeholder="–" className="h-9 w-16 text-sm" data-testid={`builder-item-days-${si}-${ii}`} />
        <span className="text-[11px] text-slate-500 hidden sm:inline">days before</span>
      </div>
    )}
    <Button type="button" size="icon" variant="ghost" onClick={remove} data-testid={`builder-item-delete-${si}-${ii}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
  </div>
);

const SectionCard = ({ s, si, count, showDays, onChange, onRemove, onShift }) => {
  const setItems = (items) => onChange({ ...s, items });
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`builder-section-${si}`}>
      <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-50">
        <Input value={s.name} onChange={(e) => onChange({ ...s, name: e.target.value })} placeholder="Section name" className="h-9 font-bold text-[#24474A] bg-white" data-testid={`builder-section-name-${si}`} />
        <span className="text-xs text-slate-500 shrink-0">{s.items.length} item{s.items.length === 1 ? "" : "s"}</span>
        <Button type="button" size="icon" variant="ghost" disabled={si === 0} onClick={() => onShift(-1)} data-testid={`builder-section-up-${si}`}><ArrowUp className="w-4 h-4" /></Button>
        <Button type="button" size="icon" variant="ghost" disabled={si === count - 1} onClick={() => onShift(1)} data-testid={`builder-section-down-${si}`}><ArrowDown className="w-4 h-4" /></Button>
        <Button type="button" size="icon" variant="ghost" onClick={onRemove} data-testid={`builder-section-delete-${si}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
      </div>
      {s.items.map((it, ii) => (
        <ItemRow key={ii} item={it} si={si} ii={ii} showDays={showDays}
          update={(v) => setItems(s.items.map((x, k) => (k === ii ? v : x)))}
          remove={() => setItems(s.items.filter((_, k) => k !== ii))}
          shift={(d) => setItems(move(s.items, ii, d))} />
      ))}
      <button type="button" onClick={() => setItems([...s.items, { name: "", days_before: null }])} className="w-full flex items-center gap-1.5 px-4 py-2.5 border-t border-slate-100 text-sm font-bold text-[#24474A] hover:bg-[#EAF2F2] transition-colors" data-testid={`builder-add-item-${si}`}>
        <Plus className="w-4 h-4" /> Add item
      </button>
    </div>
  );
};

export default function ChecklistBuilder() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isNew) api.get(`/checklist-templates/${id}`).then((r) => setF(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id, isNew]);

  const setSections = (sections) => setF((x) => ({ ...x, sections }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { name: f.name, fields: f.fields, sections: f.sections };
      if (isNew) await api.post("/checklist-templates", body);
      else await api.put(`/checklist-templates/${id}`, body);
      toast.success("List saved");
      navigate("/settings/checklists");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const total = f.sections.reduce((n, s) => n + s.items.length, 0);

  return (
    <div data-testid="checklist-builder">
      <Link to="/settings/checklists" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#24474A] mb-4" data-testid="builder-back-link"><ArrowLeft className="w-4 h-4" /> All lists</Link>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-6">
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-1">{isNew ? "New pre-defined list" : "Edit pre-defined list"} · {total} items</div>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="List name, e.g. Venue opening tracker" className="h-12 text-xl font-bold text-[#24474A]" data-testid="builder-name-input" />
        </div>
        <Button className="rounded-full h-11" onClick={save} disabled={busy || !f.name.trim()} data-testid="builder-save-btn"><Save className="w-4 h-4 mr-1.5" /> Save list</Button>
      </div>
      {!isNew && <p className="text-xs text-slate-500 mb-4">Changes apply to projects that pick this list from now on. Projects already using it keep their own items and progress.</p>}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        <div className="space-y-3">
          {f.sections.map((s, si) => (
            <SectionCard key={si} s={s} si={si} count={f.sections.length} showDays={f.fields.due_date}
              onChange={(v) => setSections(f.sections.map((x, k) => (k === si ? v : x)))}
              onRemove={() => setSections(f.sections.filter((_, k) => k !== si))}
              onShift={(d) => setSections(move(f.sections, si, d))} />
          ))}
          <Button variant="outline" className="rounded-full" onClick={() => setSections([...f.sections, { name: "", items: [{ name: "", days_before: null }] }])} data-testid="builder-add-section-btn"><Plus className="w-4 h-4 mr-1.5" /> Add section</Button>
        </div>
        <FieldsPanel fields={f.fields} onChange={(fields) => setF({ ...f, fields })} />
      </div>
    </div>
  );
}
